import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "@material/web/textfield/outlined-text-field.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/outlined-button.js";
import "@material/web/button/text-button.js";

import { pb, hasOrganization } from "../lib/pb";
import { navigate } from "../lib/navigation";
import { consumeReturnTo } from "../router";
import { inputValue, errorMessage } from "../lib/dom";
import { sharedStyles, authStyles } from "../styles/shared";
import { providerMark } from "../lib/provider-marks";
import { stagger, fadeIn, prefersReducedMotion } from "../lib/motion";
import {
  loadSignInMethods,
  cachedSignInMethods,
  OPTIMISTIC,
  browserSupportsPasskeys,
  signInWithPasskey,
  signInWithOAuth,
  requestOtp,
  signInWithOtp,
  type SignInMethods,
} from "../lib/signin-methods";

// The sign-in page offers whatever the deployment actually has.
//
// Which methods exist is a deployment decision — a customer with their own
// identity provider gets an SSO button, one without Google credentials must not
// be shown a Google button that fails. So the page asks the server first and
// renders nothing it cannot deliver.

type Step = "identify" | "code";

@customElement("login-page")
export class LoginPage extends LitElement {
  static styles = [
    sharedStyles,
    authStyles,
    css`
      .methods {
        display: grid;
        gap: 8px;
      }
      /* Alternative sign-in methods: present, but visibly secondary to the
         primary email + password path above them. */
      .alts {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 2px 4px;
        margin-top: -4px;
      }
      /* Override the full-width button rule above: these two belong on ONE
         line, so they read as a footnote rather than a third list of choices. */
      .alts md-text-button {
        width: auto;
      }
      .divider {
        display: flex;
        align-items: center;
        gap: 12px;
        color: var(--md-sys-color-on-surface-variant);
        font-size: 0.8rem;
      }
      .divider::before,
      .divider::after {
        content: "";
        flex: 1;
        height: 1px;
        background: var(--md-sys-color-outline-variant);
      }
      .sent {
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
        padding: 12px 14px;
        border-radius: 12px;
        font-size: 0.9rem;
      }
      md-outlined-button,
      md-filled-button,
      md-text-button {
        width: 100%;
      }
      /* Buttons acknowledge a press immediately, which is what makes an
         interface feel responsive even when the network does not. */
      .provider:active:not(:disabled) {
        transform: scale(0.985);
      }
      md-outlined-button,
      md-filled-button {
        transition: opacity 140ms ease;
      }
      @media (prefers-reduced-motion: reduce) {
        .provider,
        .provider:active {
          transition: none;
          transform: none;
        }
      }
      .providers {
        display: grid;
        gap: 10px;
      }
      /* The primary action of the page. Full width, brand mark on the left,
         label optically centred — the shape people already recognise. */
      .provider {
        display: flex;
        align-items: center;
        gap: 12px;
        width: 100%;
        padding: 14px 16px;
        border-radius: 999px;
        border: 1px solid var(--md-sys-color-outline);
        background: var(--md-sys-color-surface);
        color: var(--md-sys-color-on-surface);
        font: inherit;
        font-size: 0.98rem;
        font-weight: 500;
        cursor: pointer;
        transition: background 120ms ease, border-color 120ms ease;
      }
      .provider:hover:not(:disabled) {
        background: var(--md-sys-color-surface-container-high);
      }
      .provider:focus-visible {
        outline: 3px solid var(--md-sys-color-primary);
        outline-offset: 2px;
      }
      .provider:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }
      .provider .mark {
        display: grid;
        place-items: center;
        width: 20px;
      }
      .provider .label {
        flex: 1;
        text-align: center;
      }
      .provider .pending {
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        padding: 2px 8px;
        border-radius: 999px;
        background: var(--md-sys-color-surface-container-highest);
        color: var(--md-sys-color-on-surface-variant);
      }
      /* Creating an account is a decision of its own, not a line of small print
         at the bottom — somebody who cannot sign in because they have no
         account yet must be able to see the way forward. */
      .signup {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        flex-wrap: wrap;
        margin-top: 4px;
        padding-top: 16px;
        border-top: 1px solid var(--md-sys-color-outline-variant);
        font-size: 0.9rem;
        color: var(--md-sys-color-on-surface-variant);
      }
      .method-note {
        font-size: 0.82rem;
        line-height: 1.4;
        color: var(--md-sys-color-on-surface-variant);
        margin: -2px 2px 4px;
      }
      md-text-button.secondary {
        --md-text-button-label-text-color: var(--md-sys-color-on-surface-variant);
      }
    `,
  ];

  @state() private email = "";
  @state() private password = "";
  @state() private code = "";
  @state() private otpId = "";
  @state() private step: Step = "identify";
  @state() private loading = false;
  @state() private error = "";
  // Never null, so the page has something real to draw on its first frame. A
  // spinner where a form could be is lag the user can see: the password field
  // is correct for almost every deployment, so it is shown immediately and
  // corrected — additively — when discovery lands.
  @state() private methods: SignInMethods = cachedSignInMethods() ?? OPTIMISTIC;
  private discovered = cachedSignInMethods() !== null;

  connectedCallback(): void {
    super.connectedCallback();
    void this.discover();
  }

  firstUpdated(): void {
    if (prefersReducedMotion()) return;
    const card = this.renderRoot.querySelector(".auth-card");
    (card as HTMLElement | null)?.animate(
      [
        { opacity: 0, transform: "translateY(10px) scale(0.995)" },
        { opacity: 1, transform: "none" },
      ],
      { duration: 300, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)", fill: "backwards" },
    );
  }

  private async discover(): Promise<void> {
    const next = await loadSignInMethods();
    const gained =
      next.oauth.length !== this.methods.oauth.length ||
      next.passkey !== this.methods.passkey ||
      next.otp !== this.methods.otp;
    this.methods = next;
    if (!gained || this.discovered) return;
    this.discovered = true;
    // Animate only what actually appeared. Re-animating the whole card would
    // make a correct first paint look like a reload.
    await this.updateComplete;
    stagger(this.renderRoot.querySelectorAll(".provider, .methods > *, .alts > *"));
  }

  private async done(): Promise<void> {
    // Land the user back on the URL they originally arrived at, if the auth
    // guard stashed one — an emailed invitation link, a shared deep link. A
    // full load (assign, not the SPA router) so window.location carries the
    // link's query parameters for the page that reads them.
    const returnTo = consumeReturnTo();
    if (returnTo) {
      window.location.assign(returnTo);
      return;
    }
    await navigate(hasOrganization() ? "/" : "/onboarding");
  }

  private async run(action: () => Promise<void>, fallback: string): Promise<void> {
    this.error = "";
    this.loading = true;
    try {
      await action();
    } catch (err) {
      this.error = errorMessage(err, fallback);
    } finally {
      this.loading = false;
    }
  }

  private handlePassword(e: Event): void {
    e.preventDefault();
    void this.run(async () => {
      await pb
        .collection("tenancy_users")
        .authWithPassword(this.email, this.password);
      await this.done();
    }, "Could not sign in. Check your credentials.");
  }

  private handlePasskey(): void {
    void this.run(async () => {
      if (!this.email) throw new Error("Enter your email first.");
      await signInWithPasskey(this.email);
      await this.done();
    }, "Could not sign in with a passkey.");
  }

  private handleOAuth(provider: string): void {
    void this.run(async () => {
      await signInWithOAuth(provider);
      await this.done();
    }, "Could not complete sign-in with that provider.");
  }

  private handleRequestCode(): void {
    void this.run(async () => {
      if (!this.email) throw new Error("Enter your email first.");
      this.otpId = await requestOtp(this.email);
      this.step = "code";
      await this.updateComplete;
      fadeIn(this.renderRoot.querySelector(".sent"));
      stagger(this.renderRoot.querySelectorAll("form > *"), { step: 30, cap: 4 });
    }, "Could not send a code to that address.");
  }

  private handleVerifyCode(e: Event): void {
    e.preventDefault();
    void this.run(async () => {
      await signInWithOtp(this.otpId, this.code);
      await this.done();
    }, "That code was not accepted. It may have expired.");
  }

  private renderOAuth() {
    const providers = this.methods?.oauth ?? [];
    if (providers.length === 0) return nothing;
    return html`
      <div class="providers">
        ${providers.map(
          (p) => html`
            <button
              class="provider"
              type="button"
              ?disabled=${this.loading || !p.configured}
              title=${p.configured
                ? `Sign in with ${p.displayName}`
                : `${p.displayName} is not configured yet`}
              @click=${() => this.handleOAuth(p.name)}
            >
              <span class="mark">${providerMark(p.name)}</span>
              <span class="label">Sign in with ${p.displayName}</span>
              ${!p.configured
                ? html`<span class="pending">soon</span>`
                : nothing}
            </button>
          `,
        )}
      </div>
    `;
  }

  private renderCodeStep() {
    return html`
      <form @submit=${(e: Event) => this.handleVerifyCode(e)}>
        <p class="sent">
          We sent a one-time code to <strong>${this.email}</strong>. It expires
          in a few minutes.
        </p>
        <md-outlined-text-field
          label="One-time code"
          inputmode="numeric"
          autocomplete="one-time-code"
          required
          .value=${this.code}
          @input=${(e: Event) => (this.code = inputValue(e))}
        ></md-outlined-text-field>
        ${this.error ? html`<p class="error-text">${this.error}</p>` : nothing}
        <md-filled-button type="submit" ?disabled=${this.loading}>
          ${this.loading ? "Checking…" : "Sign in"}
        </md-filled-button>
        <md-text-button
          type="button"
          ?disabled=${this.loading}
          @click=${() => {
            this.step = "identify";
            this.code = "";
            this.error = "";
          }}
        >
          Use a different method
        </md-text-button>
      </form>
    `;
  }

  private renderIdentifyStep() {
    const m = this.methods;
    const passkeyOffered = m?.passkey && browserSupportsPasskeys();
    const hasProviders = (m?.oauth.length ?? 0) > 0;
    const hasPasswordless = passkeyOffered || m?.otp;

    // Everything on offer is still VISIBLE — a method behind a click is a method
    // most people never find. What changed is WEIGHT: the page used to present
    // four equal actions (provider, passkey, one-time code, password) with an
    // email field floating between them, so nobody could tell which one was
    // "the" way in. Now there is exactly ONE primary path — email + password,
    // ending in a single filled Sign in — with passkey and one-time code as
    // quiet alternatives on one line beneath it. Same capabilities, one obvious
    // default.
    const altMethods = hasPasswordless
      ? html`
          <div class="alts">
            ${passkeyOffered
              ? html`
                  <md-text-button
                    class="secondary"
                    ?disabled=${this.loading}
                    @click=${() => this.handlePasskey()}
                  >
                    Use a passkey
                  </md-text-button>
                `
              : nothing}
            ${m?.otp
              ? html`
                  <md-text-button
                    class="secondary"
                    ?disabled=${this.loading}
                    @click=${() => this.handleRequestCode()}
                  >
                    Email me a code
                  </md-text-button>
                `
              : nothing}
          </div>
        `
      : nothing;

    return html`
      ${this.renderOAuth()}
      ${hasProviders
        ? html`<div class="divider">or sign in with email</div>`
        : nothing}

      ${m?.password
        ? html`
            <form @submit=${(e: Event) => this.handlePassword(e)}>
              <md-outlined-text-field
                label="Email"
                type="email"
                autocomplete="email webauthn"
                required
                .value=${this.email}
                @input=${(e: Event) => (this.email = inputValue(e))}
              ></md-outlined-text-field>
              <md-outlined-text-field
                label="Password"
                type="password"
                autocomplete="current-password"
                required
                .value=${this.password}
                @input=${(e: Event) => (this.password = inputValue(e))}
              ></md-outlined-text-field>
              ${this.error ? html`<p class="error-text">${this.error}</p>` : nothing}
              <md-filled-button type="submit" ?disabled=${this.loading}>
                ${this.loading ? "Signing in…" : "Sign in"}
              </md-filled-button>
              <md-text-button class="secondary" href="/reset-password">
                Forgot your password?
              </md-text-button>
            </form>
            ${altMethods}
          `
        : html`
            <!-- No password auth on this deployment: the passwordless methods
                 ARE the primary path, so they keep full weight. -->
            <md-outlined-text-field
              label="Email"
              type="email"
              autocomplete="email webauthn"
              required
              .value=${this.email}
              @input=${(e: Event) => (this.email = inputValue(e))}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key !== "Enter") return;
                if (passkeyOffered) this.handlePasskey();
                else if (m?.otp) this.handleRequestCode();
              }}
            ></md-outlined-text-field>
            ${hasPasswordless
              ? html`
                  <div class="methods">
                    ${passkeyOffered
                      ? html`
                          <md-filled-button
                            ?disabled=${this.loading}
                            @click=${() => this.handlePasskey()}
                          >
                            Continue with a passkey
                          </md-filled-button>
                        `
                      : nothing}
                    ${m?.otp
                      ? html`
                          <md-outlined-button
                            ?disabled=${this.loading}
                            @click=${() => this.handleRequestCode()}
                          >
                            Email me a one-time code
                          </md-outlined-button>
                        `
                      : nothing}
                  </div>
                `
              : nothing}
            ${this.error ? html`<p class="error-text">${this.error}</p>` : nothing}
          `}
    `;
  }

  render() {
    return html`
      <div class="auth-page">
        <div class="auth-card">
          <h1>Sign in</h1>
          <p class="subtitle">
            Manage your organization's Digital Product Passports.
          </p>
          ${this.step === "code"
            ? this.renderCodeStep()
            : this.renderIdentifyStep()}
          <div class="signup">
            <span>New to Authentrace?</span>
            <md-text-button href="/signup">Create an account</md-text-button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "login-page": LoginPage;
  }
}
